"use client";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Minus, Plus, Sparkles } from "lucide-react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Product as ProductType, Variant } from "../interfaces/products";
import { useState } from "react";
import cart from "observables/cart";
import noop from "lodash/noop";
import { observer } from "mobx-react-lite";
import RadioButtons from "./radio-buttons";
import CataloguePicture from "./catalogue-picture";
import { Badge } from "components/ui/badge";

interface Props {
  product: ProductType;
  className?: string;
  shouldDisplayIngredients: boolean;
  onSelectVariant?: (variantId: number) => void;
}

const Product = ({
  product,
  className,
  shouldDisplayIngredients,
  onSelectVariant,
}: Props) => {
  const [quantity, setQuantity] = useState("1");
  const [selectedVariant, setSelectedVariant] = useState<Variant>();
  const [isAdding, setIsAdding] = useState(false);

  const handleSelectVariant = (value: string) => {
    const newSelectedVariant = product.variants.find(
      (variant) => String(variant.id) === value
    );

    if (!newSelectedVariant) return;

    setSelectedVariant(newSelectedVariant);

    onSelectVariant?.(Number(value));
  };

  const handleUpdateQuantity = (shouldAdd: boolean) => () => {
    if (shouldAdd) {
      setQuantity((previousQuantity) =>
        String(Number(previousQuantity || 0) + 1)
      );
      return;
    }

    if (Number(quantity || 0) <= 1) return;

    setQuantity((previousQuantity) =>
      String(Number(previousQuantity || 0) - 1)
    );
  };

  const handleClickAddProduct = async () => {
    if (!/^\d+$/.test(quantity)) return;

    const variant =
      product.variants.length === 1 ? product.variants[0] : selectedVariant;

    if (!variant) return;

    // Start animation
    setIsAdding(true);

    cart.addElement({
      itemId: variant.id,
      variantId: variant.id,
      variantName: variant.name,
      productId: Number(product.id),
      productName: product.name,
      productType: product.type,
      quantity: Number(quantity),
      priceAfterVatAfterPromotion:
        Number(variant.priceAfterVat) * (1 - cart.promotion / 100),
      priceAfterVatBeforePromotion: Number(variant.priceAfterVat),
    });

    // Reset animation after delay
    setTimeout(() => {
      setIsAdding(false);
    }, 500);
  };

  const currentVariant = selectedVariant || product.variants[0];
  const hasPromotion = cart.promotion > 0;

  return (
    <Card 
      key={product.name} 
      className={`
        group relative overflow-hidden transition-all duration-300 ease-in-out
        hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1
        border-0 bg-gradient-to-br from-white via-gray-50/50 to-white
        dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900
        backdrop-blur-sm
        ${isAdding ? 'shadow-2xl shadow-green-500/25 ring-2 ring-green-400/50' : ''}
        ${className}
      `}
    >
      {/* Promotion badge */}
      {hasPromotion && (
        <div className="absolute top-3 left-3 z-20">
          <Badge 
            variant="secondary" 
            className="bg-gradient-to-r from-red-500 to-pink-500 text-white border-0 shadow-lg"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            -{cart.promotion}%
          </Badge>
        </div>
      )}

      {/* Large Cover Image Section with Overlaid Content */}
      {product.items && shouldDisplayIngredients && (
        <div className="relative h-64 md:h-72 overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700">
          {/* Background Image */}
          <div className="absolute inset-0 w-full h-full">
            <div className="w-full h-full [&>*]:w-full [&>*]:h-full [&>*]:object-cover">
              <CataloguePicture
                product={product}
                selectedVariant={selectedVariant}
              />
            </div>
          </div>
          
          {/* Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
          
          {/* Overlaid Content */}
          <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-between">
            {/* Top Section - Product Info */}
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <CardTitle className="text-xl md:text-2xl font-bold text-white drop-shadow-lg">
                  {product.name}
                </CardTitle>
                <CardDescription className="text-sm md:text-base font-medium text-blue-200 mt-1 drop-shadow">
                  {product.type.name}
                </CardDescription>
              </div>
              
              {currentVariant && (
                <div className="text-right">
                  {hasPromotion ? (
                    <div className="space-y-1">
                      <div className="text-sm md:text-base text-gray-300 line-through drop-shadow">
                        {currentVariant.priceAfterVat}€
                      </div>
                      <div className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                        {(Number(currentVariant.priceAfterVat) * (1 - cart.promotion / 100)).toFixed(2)}€
                      </div>
                    </div>
                  ) : (
                    <div className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                      {currentVariant.priceAfterVat}€
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bottom Section - Ingredients */}
            <div className="space-y-3">
              <div className="text-xs md:text-sm font-semibold text-white/90 uppercase tracking-wide drop-shadow">
                Ingrédients
              </div>
              <div className="flex flex-wrap gap-1.5">
                {product.items.slice(0, 5).map((item) => (
                  <Badge 
                    key={item.productItem.name}
                    variant="secondary"
                    className="text-xs bg-white/25 backdrop-blur-sm text-white border-white/40 hover:bg-white/35 transition-colors duration-200 shadow-lg"
                  >
                    {item.productItem.name}
                  </Badge>
                ))}
                {selectedVariant && shouldDisplayIngredients && (
                  <>
                    {selectedVariant.items.slice(0, 3).map((item) => (
                      <Badge 
                        key={item.productItem.name}
                        variant="secondary"
                        className="text-xs bg-blue-500/35 backdrop-blur-sm text-white border-blue-300/60 hover:bg-blue-500/45 transition-colors duration-200 shadow-lg"
                      >
                        {item.productItem.name}
                      </Badge>
                    ))}
                  </>
                )}
                {(product.items.length + (selectedVariant?.items?.length || 0)) > 8 && (
                  <Badge 
                    variant="secondary"
                    className="text-xs bg-white/25 backdrop-blur-sm text-white border-white/40 shadow-lg"
                  >
                    +{(product.items.length + (selectedVariant?.items?.length || 0)) - 8}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fallback for products without images */}
      {(!product.items || !shouldDisplayIngredients) && (
        <CardHeader className="relative z-10">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <CardTitle className="text-lg font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                {product.name}
              </CardTitle>
              <CardDescription className="text-sm font-medium text-blue-600/80 dark:text-blue-400/80 mt-1">
                {product.type.name}
              </CardDescription>
            </div>
            
            {currentVariant && (
              <div className="text-right">
                {hasPromotion ? (
                  <div className="space-y-1">
                    <div className="text-sm text-gray-400 dark:text-gray-500 line-through">
                      {currentVariant.priceAfterVat}€
                    </div>
                    <div className="text-2xl font-bold bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent">
                      {(Number(currentVariant.priceAfterVat) * (1 - cart.promotion / 100)).toFixed(2)}€
                    </div>
                  </div>
                ) : (
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {currentVariant.priceAfterVat}€
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>
      )}

      <CardFooter className="relative z-10 flex flex-col space-y-4 pt-4">
        {/* Variant selection */}
        {product.variants.length > 1 && (
          <div className="w-full">
            <RadioButtons
              onSelectValue={handleSelectVariant}
              options={product.variants.map((variant) => ({
                value: String(variant.id),
                displayName: variant.name,
              }))}
              selectedValue={String(selectedVariant?.id)}
            />
          </div>
        )}

        {/* Quantity controls */}
        <div className="flex items-center justify-center space-x-3">
          <Button
            variant="outline"
            size="icon"
            onClick={handleUpdateQuantity(false)}
            className="h-10 w-10 rounded-full border-2 hover:border-blue-500 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400 transition-all duration-200"
          >
            <Minus className="h-4 w-4" />
          </Button>
          
          <div className="relative">
            <Input
              type="text"
              className="w-16 h-10 text-center border-2 rounded-lg font-semibold text-lg focus:border-blue-500 dark:focus:border-blue-400 transition-all duration-200"
              value={quantity}
              onChange={({ target: { value } }) => {
                if (value === "") {
                  setQuantity("");
                  return;
                }

                if (!/^\d+$/.test(value)) return;

                if (Number(value) < 1) {
                  setQuantity("1");
                  return;
                }

                setQuantity(value);
              }}
            />
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleUpdateQuantity(true)}
            className="h-10 w-10 rounded-full border-2 hover:border-blue-500 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400 transition-all duration-200"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Add to cart button */}
        <Button
          onClick={handleClickAddProduct}
          disabled={product.variants.length > 1 && !selectedVariant}
          className={`
            w-full h-12 font-semibold rounded-lg shadow-lg hover:shadow-xl 
            transition-all duration-500 transform disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
            ${isAdding
              ? `bg-gradient-to-r from-green-400 via-green-500 to-green-600 
                 animate-pulse shadow-2xl shadow-green-500/50 
                 ring-4 ring-green-400/30 ring-offset-2 ring-offset-white dark:ring-offset-gray-900
                 scale-105 hover:scale-105`
              : `bg-gradient-to-r from-blue-600 to-blue-700 
                 hover:from-blue-700 hover:to-blue-800 
                 dark:from-blue-700 dark:to-blue-800 dark:hover:from-blue-800 dark:hover:to-blue-900 
                 hover:scale-[1.02] hover:shadow-blue-500/25`
            } 
            text-white relative overflow-hidden
          `}
        >
          {/* Shimmer effect overlay */}
          <div className={`absolute inset-0 ${isAdding ? 'animate-pulse' : 'opacity-0'} bg-gradient-to-r from-transparent via-white/20 to-transparent transform skew-x-12`}></div>
          
          {isAdding ? (
            <div className="flex items-center justify-center relative z-10">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  {/* Checkmark with expanding circle */}
                  <div className="w-5 h-5 rounded-full bg-white/20 animate-ping absolute"></div>
                  <svg 
                    className="w-5 h-5 text-white animate-bounce" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={3} 
                      d="M5 13l4 4L19 7"
                      className="animate-pulse"
                    />
                  </svg>
                </div>
                <span className="animate-bounce font-bold tracking-wide">
                  Ajouté !
                </span>
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-white rounded-full animate-bounce"></div>
                  <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center relative z-10 group-hover:scale-105 transition-transform duration-200">
              <Plus className="w-4 h-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
              <span className="tracking-wide">Ajouter au panier</span>
            </div>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default observer(Product);
